import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  Divider,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  MdVisibility, MdVisibilityOff, MdLock, MdPersonOutline, MdEmail, MdHowToReg,
} from 'react-icons/md';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEOMeta from '../components/SEOMeta/SEOMeta';
import logo from '../logo.svg';

const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 6) score += 1;
  if (password.length >= 10) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;
  return score;
};

const strengthLabel = ['', 'Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
const strengthColor = ['', '#d32f2f', '#f57c00', '#fbc02d', '#388e3c', '#1b5e20'];

const SignupPage = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    username: '', email: '', password: '', confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const passwordStrength = getPasswordStrength(form.password);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { username, email, password, confirmPassword } = form;
    if (!username.trim() || !email.trim() || !password || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (password.length < 4) {
      setError('Password must be at least 4 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      signup(username.trim(), email.trim().toLowerCase(), password);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #0b0c10 0%, #1a1b2e 50%, #16213e 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <SEOMeta
          title="Admin Registration | Algodoor Training Platform"
          description="Create an admin account to manage courses and students."
          keywords="admin registration, instructor signup, account creation"
          url="/signup"
          type="WebPage"
          noindex
        />
        <Card
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 440,
            borderRadius: 4,
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Avatar
              sx={{
                mx: 'auto',
                mb: 2,
                width: 68,
                height: 68,
                background: 'linear-gradient(135deg, #AE67FA, #F49867)',
                boxShadow: '0 4px 20px rgba(174,103,250,0.4)',
              }}
            >
              <MdHowToReg style={{ fontSize: 36, color: 'white' }} />
            </Avatar>
            <Typography variant="h5" fontWeight={700} color="white" mb={1.5}>
              Request Submitted!
            </Typography>
            <Typography variant="body1" color="rgba(255,255,255,0.6)" mb={3}>
              Your signup request has been sent to the admin for review.
              You will be able to log in once your account is approved.
            </Typography>
            <Alert
              severity="info"
              sx={{
                mb: 3,
                borderRadius: 2,
                background: 'rgba(2,136,209,0.15)',
                border: '1px solid rgba(2,136,209,0.3)',
                color: '#81d4fa',
                '& .MuiAlert-icon': { color: '#81d4fa' },
                textAlign: 'left',
              }}
            >
              <strong>Username:</strong> {form.username}
              <br />
              <strong>Email:</strong> {form.email}
            </Alert>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={primaryBtnStyle}
            >
              Go to Login
            </Button>
            <Box sx={{ mt: 2 }}>
              <Link to="/" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', textDecoration: 'none' }}>
                ← Back to Home
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0b0c10 0%, #1a1b2e 50%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <SEOMeta
        title="Admin Registration | Algodoor Training Platform"
        description="Create an admin account to manage courses and students."
        keywords="admin registration, instructor signup, account creation"
        url="/signup"
        type="WebPage"
        noindex
      />
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 440,
          borderRadius: 4,
          background: 'rgba(255,255,255,0.04)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              src={logo}
              alt="Algodoor"
              sx={{ height: 36, mb: 2, filter: 'brightness(0) invert(1)' }}
            />
            <Avatar
              sx={{
                mx: 'auto',
                mb: 1.5,
                width: 56,
                height: 56,
                background: 'linear-gradient(135deg, #AE67FA, #F49867)',
                boxShadow: '0 4px 20px rgba(174,103,250,0.4)',
              }}
            >
              <MdHowToReg style={{ fontSize: 28, color: 'white' }} />
            </Avatar>
            <Typography variant="h5" fontWeight={700} color="white" letterSpacing={0.5}>
              Create Account
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.5)" mt={0.5}>
              Request access to Algodoor platform
            </Typography>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                background: 'rgba(211,47,47,0.15)',
                border: '1px solid rgba(211,47,47,0.3)',
                color: '#ff8a8a',
                '& .MuiAlert-icon': { color: '#ff8a8a' },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              name="username"
              label="Username"
              value={form.username}
              onChange={handleChange}
              autoComplete="username"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdPersonOutline style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldStyle}
              margin="dense"
            />
            <TextField
              fullWidth
              name="email"
              label="Email Address"
              type="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdEmail style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldStyle}
              margin="dense"
            />
            <TextField
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdLock style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.4)' }}
                    >
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldStyle}
              margin="dense"
            />

            {form.password && (
              <Box sx={{ mt: 0.5, mb: 0.5, px: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={(passwordStrength / 5) * 100}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: strengthColor[passwordStrength],
                      borderRadius: 2,
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: strengthColor[passwordStrength], mt: 0.3, display: 'block' }}>
                  {strengthLabel[passwordStrength]}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type={showConfirm ? 'text' : 'password'}
              value={form.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MdLock style={{ color: 'rgba(255,255,255,0.4)', fontSize: '1.2rem' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirm((v) => !v)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.4)' }}
                    >
                      {showConfirm ? <MdVisibilityOff /> : <MdVisibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldStyle}
              margin="dense"
              error={!!form.confirmPassword && form.password !== form.confirmPassword}
              helperText={
                form.confirmPassword && form.password !== form.confirmPassword
                  ? 'Passwords do not match'
                  : ''
              }
              FormHelperTextProps={{ sx: { color: '#ff8a8a', ml: 0 } }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ ...primaryBtnStyle, mt: 2 }}
            >
              {loading ? 'Submitting…' : 'Request Access'}
            </Button>
          </Box>

          <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.1)' }}>
            <Typography variant="caption" color="rgba(255,255,255,0.35)">OR</Typography>
          </Divider>

          <Typography variant="body2" align="center" color="rgba(255,255,255,0.5)">
            Already have an account?&nbsp;
            <Link to="/login" style={{ color: '#ff6b6b', fontWeight: 600, textDecoration: 'none' }}>
              Sign In
            </Link>
          </Typography>
          <Box sx={{ mt: 1.5, textAlign: 'center' }}>
            <Link to="/" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', textDecoration: 'none' }}>
              ← Back to Home
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

const fieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#AE67FA' },
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderRadius: 2,
  },
  '& .MuiInputLabel-root': {
    color: 'rgba(255,255,255,0.45)',
    '&.Mui-focused': { color: '#f8c7ac' },
  },
};

const primaryBtnStyle = {
  py: 1.4,
  borderRadius: 2.5,
  background: 'linear-gradient(135deg, #AE67FA 0%, #F49867 100%)',
  color: '#120a20',
  fontWeight: 700,
  fontSize: '1rem',
  letterSpacing: 0.5,
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(174,103,250,0.38)',
  '&:hover': {
    background: 'linear-gradient(135deg, #b579fb 0%, #f6a67b 100%)',
    boxShadow: '0 6px 25px rgba(174,103,250,0.5)',
  },
  '&:disabled': { opacity: 0.6 },
};

export default SignupPage;
